import React, { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Table } from 'reactstrap';
import { DealerForm, DealerFullModel } from '../../../../shared/dealer';
import { Option, OptionDataPayload, OptionDataValue } from '../../../../shared/options';
import { ProductOptionRenderer } from '../../../components';
import { OptionSimpleDisplay } from '../../../components/option/OptionSimpleDisplay';
import { useTranslation } from '../../../translations';
import { renderName } from '../../../utils';
import { EditEntry } from '.';

interface DealerEditFormProps {
  readonly dealer?: DealerFullModel;
  readonly edit: boolean;
  readonly form: DealerForm;
  readonly options: OptionDataPayload;
  readonly setOptions: Dispatch<SetStateAction<OptionDataPayload>>;
}

export const DealerEditForm: FC<DealerEditFormProps> = ({
  dealer,
  edit,
  form,
  options,
  setOptions,
}) => {
  const { ts } = useTranslation();
  return (
    <Table>
      <tbody>
        {dealer && (
          <tr>
            <th scope="row">{ts('common.user')}</th>
            <td id="userName">
              <Link to={`/housekeeping/attendees/user/${dealer.user.id}`}>
                {renderName(dealer.user)}
              </Link>{' '}
              {dealer.user.hasRestrictionNotice ? (
                <Badge className="has-restriction-notice" color="danger" key="hasRestrictionNotice">
                  {ts('common.restriction')}
                </Badge>
              ) : undefined}
            </td>
          </tr>
        )}
        <EditEntry displayName="business_name" edit={edit} name="name" value={dealer?.name} />
        <EditEntry displayName="business_email" edit={edit} name="email" value={dealer?.email} />
        <EditEntry
          displayName="common.address_line_1"
          edit={edit}
          name="addressLine1"
          value={dealer?.addressLine1}
        />
        <EditEntry
          displayName="common.address_line_2_optional"
          edit={edit}
          name="addressLine2"
          value={dealer?.addressLine2 ?? ''}
        />
        <EditEntry
          displayName="common.city"
          edit={edit}
          name="addressCity"
          value={dealer?.addressCity}
        />
        <EditEntry
          displayName="common.state_province"
          edit={edit}
          name="addressState"
          value={dealer?.addressState}
        />
        <EditEntry
          displayName="common.zip_postal_code"
          edit={edit}
          name="addressZipcode"
          value={dealer?.addressZipcode}
        />
        <EditEntry
          displayName="common.country"
          edit={edit}
          name="addressCountry"
          value={dealer?.addressCountry}
        />
        <OptionList edit={edit} form={form.options} options={options} setOptions={setOptions} />
      </tbody>
    </Table>
  );
};

interface OptionListProps {
  readonly edit: boolean;
  readonly form: Option[];
  readonly options: OptionDataPayload;
  readonly setOptions: Dispatch<SetStateAction<OptionDataPayload>>;
}

const OptionList: FC<OptionListProps> = ({ edit, form, options, setOptions }) => {
  const onOptionChange = useCallback(
    (id: number, value: OptionDataValue) => {
      setOptions((old) => ({
        ...old,
        [id.toString()]: value,
      }));
    },
    [setOptions],
  );

  return form.map((option) => {
    const value = options[option.id];

    if (!value && !edit) {
      return null;
    }

    return (
      <tr key={option.id}>
        <th scope="row">{option.name}</th>
        <td id={`dealer-${option.id}`}>
          {edit ? (
            <ProductOptionRenderer
              defaultValue={value}
              onChange={onOptionChange}
              option={option}
              required={false}
            />
          ) : (
            <OptionSimpleDisplay option={option} value={value} />
          )}
        </td>
      </tr>
    );
  });
};
